import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { ExternalBrowser } from '@/lib/external-browser';

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/** The iOS app, which has neither the GPS timer nor Google sign-in (docs/IOS.md). */
export function isIos(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

/**
 * Opens a page that is not ours in its own window — today, the way out of the
 * embedded donation form, for a supporter who would rather pay on HelloAsso's
 * own page, address bar and all, or whose browser will not show the frame.
 *
 * On the web a plain new tab is enough. In the Android WebView it has to be a
 * Custom Tab, so the page renders in the system browser with its own address
 * bar rather than inside our shell.
 */
export async function openExternalUrl(url: string): Promise<void> {
  if (isNativePlatform()) {
    await Browser.open({ url });
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

/** A payment page the app opened in a Custom Tab, or in Safari on iOS. */
export interface PaymentTab {
  /** Closes the tab and brings the app back, without running `onBack`. Safari stays open. */
  close: () => Promise<void>;
}

/**
 * Hands the supporter over to HelloAsso's Checkout page.
 *
 * On the web it is a plain navigation, as HelloAsso's documentation
 * describes: the payment page sends the supporter back to our return page,
 * and there is no tab to hand back (`null`).
 *
 * In the Android app the page opens in a Custom Tab, and `onBack` runs once,
 * when the supporter is back in the app without the app having closed the
 * tab. Browsers report a closed tab unevenly — Firefox sends nothing — so the
 * app coming back to the foreground counts as well.
 *
 * In the iOS app the page opens in Safari itself, not in a tab inside the app:
 * App Review wants donations to a charity collected outside the app
 * (guideline 3.2.2). The app coming back to the foreground is then the only
 * signal, and the app cannot close Safari.
 */
export async function openPaymentPage(url: string, onBack: () => void): Promise<PaymentTab | null> {
  if (!isNativePlatform()) {
    window.location.assign(url);
    return null;
  }

  let isOver = false;
  const listeners = await Promise.all([
    ...(isIos() ? [] : [Browser.addListener('browserFinished', () => end(onBack))]),
    App.addListener('resume', () => end(onBack)),
  ]);
  function end(then?: () => void) {
    if (isOver) return;
    isOver = true;
    for (const listener of listeners) void listener.remove();
    then?.();
  }

  if (isIos()) {
    await ExternalBrowser.open({ url });

    return { close: async () => end() };
  }

  await Browser.open({ url });

  return {
    close: async () => {
      end();
      await Browser.close();
    },
  };
}
